/**
 * DCMS Calendar — behaviour of the Calendar UI component
 * (AMPD\DCMS2\UI\Components\Calendar, React: @dcms2/react-ui <Calendar>).
 *
 * The component renders `<div data-dcms-event-calendar>` with a JSON config in
 * `<script type="application/json" class="dcms-calendar-config">`. This module:
 *  - lazy-loads FullCalendar (standard bundle) and its locale from `config.libUrl`
 *    only when a calendar is on the page — the library is not part of any bundle,
 *  - draws the grid, switches to the mobile layout below `mobileBreakpoint`
 *    (measured on the container, so it also works in narrow dashboard columns),
 *  - filters events by the source toggles and notifies `sourceToggleUrl`,
 *  - opens event / "add event" URLs in a DCMS iframe modal and refetches after it closes,
 *  - emits DOM events on the root element (all bubble):
 *      dcms:calendar:ready        { calendar }
 *      dcms:calendar:eventclick   { event, jsEvent }          cancelable — preventDefault() skips the URL
 *      dcms:calendar:select       { start, end, startStr, endStr, allDay }
 *      dcms:calendar:change       { event, oldEvent, revert }  drag / resize
 *      dcms:calendar:datesset     { start, end, startStr, endStr, view }
 *      dcms:calendar:sourcetoggle { id, active }
 *
 * Auto-initialised on DOMContentLoaded and for nodes injected later (AJAX,
 * formBuilder, deferred render) via MutationObserver. Public API:
 * `window.DCMSCalendarEngine.init(el|id, config?)`, `.get(el|id)`, `.refetch(el|id)`, `.destroy(el|id)`.
 *
 * NOTE: `[data-dcms-calendar]` (without "event") is the DateTimePicker range
 * calendar (dcms-daterange-calendar.js) — a different component. And
 * `window.DCMSCalendar` is the formBuilder builder class (DCMS<Component>
 * convention) — the engine API lives in `window.DCMSCalendarEngine`.
 *
 * A DCMS dataset form saved in a modal opened by the calendar broadcasts
 * `dcms:recordset:refresh`; the calendar then closes the modal, shows the toast
 * and refetches (a dashboard has no list that would do it).
 */

const ROOT_SELECTOR = '[data-dcms-event-calendar]';
const ENGINE_FILE = 'index.global.min.js';
// Starting iframe height; the modal then follows the height of the page inside
const MODAL_INITIAL_HEIGHT = 240;

const FALLBACK_TEXTS = {
    today: 'Today',
    month: 'Month',
    week: 'Week',
    day: 'Day',
    list: 'Agenda',
    year: 'Year',
    prev: 'Previous',
    next: 'Next',
    allDay: 'All day',
    noEvents: 'No events to display',
    more: '+{count} more',
    weekShort: 'W',
    loadError: 'Could not load events.',
    engineError: 'Could not load the calendar.',
};

/** @type {Map<string, Promise<void>>} engine loads, keyed by directory URL */
const engineLoads = new Map();
/** @type {Map<string, Promise<void>>} locale loads, keyed by directory URL + code */
const localeLoads = new Map();
/** @type {WeakMap<HTMLElement, DCMSEventCalendar>} */
const instances = new WeakMap();

let modalCallbackCounter = 0;

function withTrailingSlash(url) {
    return url.endsWith('/') ? url : url + '/';
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.dataset.dcmsCalendarLib = '1';
        script.onload = () => resolve();
        script.onerror = () => {
            script.remove();
            reject(new Error('Cannot load ' + src));
        };
        document.head.appendChild(script);
    });
}

/**
 * Loads the FullCalendar global bundle (once per document) and the locale.
 * A missing locale file is not fatal — the engine falls back to English and the
 * DCMS texts passed in the config still apply.
 *
 * @param {string} libUrl Directory with index.global.min.js and locales/
 * @param {string} locale Two-letter language code
 * @returns {Promise<object>} window.FullCalendar
 */
export function ensureEngine(libUrl, locale) {
    const base = withTrailingSlash(libUrl);

    let load = engineLoads.get(base);
    if (!load) {
        load = (window.FullCalendar && window.FullCalendar.Calendar)
            ? Promise.resolve()
            : loadScript(base + ENGINE_FILE);
        engineLoads.set(base, load);
        load.catch(() => engineLoads.delete(base));
    }

    return load.then(() => {
        const FC = window.FullCalendar;
        if (!FC || !FC.Calendar) {
            throw new Error('FullCalendar global is missing after loading ' + base + ENGINE_FILE);
        }
        if (!locale || locale === 'en' || (FC.globalLocales || []).some((l) => l.code === locale)) {
            return FC;
        }
        const key = base + '|' + locale;
        let localeLoad = localeLoads.get(key);
        if (!localeLoad) {
            localeLoad = loadScript(base + 'locales/' + encodeURIComponent(locale) + '.global.min.js').catch(() => {});
            localeLoads.set(key, localeLoad);
        }
        return localeLoad.then(() => FC);
    });
}

function readConfig(root) {
    const script = root.querySelector(':scope > script.dcms-calendar-config');
    if (!script) {
        return {};
    }
    try {
        return JSON.parse(script.textContent || '{}');
    } catch (e) {
        console.error('[dcms-calendar] invalid config JSON', root, e);
        return {};
    }
}

function eventSourceId(event) {
    if (event == null) return null;
    if (event.sourceId != null) return String(event.sourceId);
    if (event.extendedProps && event.extendedProps.sourceId != null) return String(event.extendedProps.sourceId);
    return null;
}

/**
 * One calendar instance bound to a root element.
 */
export class DCMSEventCalendar {

    /**
     * @param {HTMLElement} root Element with [data-dcms-event-calendar]
     * @param {object} [config] Config overriding the embedded JSON (React passes props here)
     */
    constructor(root, config) {
        this.root = root;
        this.config = Object.assign(this.defaults(), config || readConfig(root));
        this.texts = Object.assign({}, FALLBACK_TEXTS, this.config.texts || {});
        this.grid = root.querySelector(':scope > .dcms-calendar-grid');
        this.errorBox = root.querySelector(':scope > .dcms-calendar-error');
        this.calendar = null;
        this.layout = null;
        this.desktopView = null;
        this.hiddenSources = new Set(
            (this.config.sources || []).filter((s) => s && s.active === false).map((s) => String(s.id))
        );
        this.feedCache = null;
        this.useCacheOnce = false;
        this.resizeObserver = null;
        this.modalCallbackName = null;
        this.modalOpen = false;
        this.messageListener = (event) => this.onWindowMessage(event);
        window.addEventListener('message', this.messageListener);
        this.destroyed = false;
        this.ready = this.start().catch((e) => {
            console.error('[dcms-calendar] render failed', this.root, e);
            this.showError(this.texts.engineError);
            this.setBusy(false);
        });
    }

    defaults() {
        return {
            libUrl: '/DCMS2/libs/fullcalendar/6.1.21/',
            locale: 'en',
            initialView: 'dayGridMonth',
            views: ['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'listWeek'],
            mobileView: 'listWeek',
            mobileBreakpoint: 640,
            initialDate: null,
            height: 'auto',
            timeGridHeight: 720,
            slotMinTime: '00:00:00',
            slotMaxTime: '24:00:00',
            scrollTime: '07:00:00',
            selectable: false,
            editable: false,
            nowIndicator: true,
            weekNumbers: false,
            dayMaxEvents: true,
            events: [],
            eventsUrl: null,
            eventsParams: {},
            sources: [],
            sourceToggleUrl: null,
            addButton: null,
            linkTarget: 'modal',
            modalWidth: '900px',
            modalBorderBottom: true,
            texts: {},
        };
    }

    async start() {
        if (!this.grid) {
            console.error('[dcms-calendar] missing .dcms-calendar-grid', this.root);
            return;
        }
        this.bindSources();

        let FC;
        try {
            FC = await ensureEngine(this.config.libUrl, this.config.locale);
        } catch (e) {
            console.error('[dcms-calendar] engine load failed', e);
            this.showError(this.texts.engineError);
            this.setBusy(false);
            return;
        }
        if (this.destroyed) {
            return;
        }

        this.grid.textContent = '';
        this.layout = this.isNarrow() ? 'mobile' : 'desktop';
        this.calendar = new FC.Calendar(this.grid, this.buildOptions());
        this.calendar.render();
        this.applyViewHeight(this.calendar.view.type);
        this.observeSize();
        this.emit('ready', { calendar: this.calendar });
    }

    // ── options ──────────────────────────────────────────────────────────────

    buildOptions() {
        const cfg = this.config;
        const t = this.texts;
        const mobile = this.layout === 'mobile';
        const initialView = mobile && cfg.mobileView ? cfg.mobileView : cfg.initialView;
        this.desktopView = cfg.initialView;

        const options = {
            initialView,
            locale: cfg.locale || 'en',
            height: cfg.height === 'auto' ? 'auto' : Number(cfg.height) || 'auto',
            slotMinTime: cfg.slotMinTime,
            slotMaxTime: cfg.slotMaxTime,
            scrollTime: cfg.scrollTime,
            nowIndicator: !!cfg.nowIndicator,
            weekNumbers: !!cfg.weekNumbers,
            dayMaxEvents: cfg.dayMaxEvents,
            selectable: !!cfg.selectable,
            selectMirror: !!cfg.selectable,
            editable: !!cfg.editable,
            // Always HH:MM — the locale default omits ":00" ("08" next to "9:30")
            eventTimeFormat: { hour: '2-digit', minute: '2-digit', meridiem: false },
            navLinks: true,
            fixedWeekCount: false,
            buttonText: {
                today: t.today,
                month: t.month,
                week: t.week,
                day: t.day,
                list: t.list,
                year: t.year,
            },
            buttonHints: {
                prev: t.prev,
                next: t.next,
                today: t.today,
            },
            allDayText: t.allDay,
            noEventsText: t.noEvents,
            weekText: t.weekShort,
            moreLinkText: (count) => String(t.more).replace('{count}', String(count)),
            customButtons: {},
            headerToolbar: this.headerToolbar(mobile),
            footerToolbar: this.footerToolbar(mobile),
            events: (info, success, failure) => {
                this.fetchEvents(info).then(
                    (list) => {
                        this.hideError();
                        success(this.filterEvents(list));
                    },
                    (err) => {
                        console.error('[dcms-calendar] events load failed', err);
                        this.showError(t.loadError);
                        failure(err);
                    }
                );
            },
            loading: (isLoading) => this.setBusy(isLoading),
            eventClick: (info) => this.onEventClick(info),
            eventDidMount: (info) => this.decorateEvent(info),
            datesSet: (info) => {
                this.applyViewHeight(info.view.type);
                this.emit('datesset', {
                    start: info.start,
                    end: info.end,
                    startStr: info.startStr,
                    endStr: info.endStr,
                    view: info.view.type,
                });
            },
            select: (info) => {
                this.emit('select', {
                    start: info.start,
                    end: info.end,
                    startStr: info.startStr,
                    endStr: info.endStr,
                    allDay: info.allDay,
                });
            },
            eventDrop: (info) => this.emit('change', { event: info.event, oldEvent: info.oldEvent, revert: info.revert }),
            eventResize: (info) => this.emit('change', { event: info.event, oldEvent: info.oldEvent, revert: info.revert }),
        };

        if (cfg.initialDate) {
            options.initialDate = cfg.initialDate;
        }
        if (cfg.addButton && cfg.addButton.url) {
            options.customButtons.dcmsAdd = {
                text: cfg.addButton.label,
                hint: cfg.addButton.label,
                click: () => this.openUrl(cfg.addButton.url, cfg.addButton.modalTitle || cfg.addButton.label),
            };
        }

        return options;
    }

    viewSwitch() {
        const views = Array.isArray(this.config.views) ? this.config.views : [];
        return views.length > 1 ? views.join(',') : '';
    }

    headerToolbar(mobile) {
        const add = this.config.addButton && this.config.addButton.url ? ' dcmsAdd' : '';
        if (mobile) {
            return { left: 'prev,next', center: 'title', right: 'today' };
        }
        return { left: 'prev,next today' + add, center: 'title', right: this.viewSwitch() };
    }

    footerToolbar(mobile) {
        if (!mobile) {
            return false;
        }
        const add = this.config.addButton && this.config.addButton.url ? 'dcmsAdd' : '';
        const views = this.viewSwitch();
        return add || views ? { left: add, center: '', right: views } : false;
    }

    /**
     * Month/list views use `config.height`, time grids a fixed scrolling box.
     */
    applyViewHeight(viewType) {
        if (!this.calendar) return;
        const wanted = String(viewType).startsWith('timeGrid')
            ? Number(this.config.timeGridHeight) || 720
            : (this.config.height === 'auto' ? 'auto' : Number(this.config.height) || 'auto');
        if (this.calendar.getOption('height') !== wanted) {
            this.calendar.setOption('height', wanted);
        }
    }

    // ── responsive layout ────────────────────────────────────────────────────

    isNarrow() {
        const width = this.root.clientWidth;
        return width > 0 && width < (Number(this.config.mobileBreakpoint) || 0);
    }

    observeSize() {
        if (typeof ResizeObserver === 'undefined') return;
        this.resizeObserver = new ResizeObserver(() => {
            if (!this.calendar) return;
            const layout = this.isNarrow() ? 'mobile' : 'desktop';
            if (layout !== this.layout) {
                this.switchLayout(layout);
            }
            this.calendar.updateSize();
        });
        this.resizeObserver.observe(this.root);
    }

    switchLayout(layout) {
        const mobile = layout === 'mobile';
        this.layout = layout;
        this.calendar.setOption('headerToolbar', this.headerToolbar(mobile));
        this.calendar.setOption('footerToolbar', this.footerToolbar(mobile));

        const mobileView = this.config.mobileView;
        if (!mobileView) return;
        if (mobile) {
            this.desktopView = this.calendar.view.type;
            if (this.desktopView !== mobileView) this.calendar.changeView(mobileView);
        } else if (this.desktopView && this.calendar.view.type === mobileView) {
            this.calendar.changeView(this.desktopView);
        }
    }

    // ── events ───────────────────────────────────────────────────────────────

    fetchEvents(info) {
        const inline = Array.isArray(this.config.events) ? this.config.events : [];
        if (!this.config.eventsUrl) {
            return Promise.resolve(inline);
        }

        const url = new URL(this.config.eventsUrl, document.baseURI);
        Object.entries(this.config.eventsParams || {}).forEach(([key, value]) => {
            if (value !== null && value !== undefined) url.searchParams.set(key, String(value));
        });
        url.searchParams.set('start', info.startStr);
        url.searchParams.set('end', info.endStr);
        const key = url.toString();

        if (this.useCacheOnce && this.feedCache && this.feedCache.key === key) {
            this.useCacheOnce = false;
            return Promise.resolve(inline.concat(this.feedCache.list));
        }
        this.useCacheOnce = false;

        return fetch(key, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then((response) => {
                if (!response.ok) throw new Error('HTTP ' + response.status);
                return response.json();
            })
            .then((data) => {
                const list = Array.isArray(data) ? data : (data && Array.isArray(data.events) ? data.events : []);
                this.feedCache = { key, list };
                return inline.concat(list);
            });
    }

    /**
     * Drops events of hidden sources; an event without its own colour takes the
     * colour of its source, so one setting colours both the toggle and the events.
     */
    filterEvents(list) {
        const sourceColors = this.sourceColors || (this.sourceColors = new Map(
            (this.config.sources || [])
                .filter((s) => s && s.color)
                .map((s) => [String(s.id), s.color])
        ));

        const result = [];
        list.forEach((event) => {
            const id = eventSourceId(event);
            if (id !== null && this.hiddenSources.has(id)) return;
            if (id !== null && sourceColors.has(id) && !event.color && !event.backgroundColor) {
                event = Object.assign({}, event, { color: sourceColors.get(id) });
            }
            result.push(event);
        });
        return result;
    }

    decorateEvent(info) {
        const parts = [info.event.title];
        const description = info.event.extendedProps ? info.event.extendedProps.description : null;
        if (description) parts.push(String(description));
        info.el.setAttribute('title', parts.join('\n'));

        const sourceId = eventSourceId(info.event);
        if (sourceId !== null) info.el.dataset.sourceId = sourceId;
    }

    onEventClick(info) {
        const proceed = this.emit('eventclick', { event: info.event, jsEvent: info.jsEvent }, true);
        if (!proceed) {
            info.jsEvent.preventDefault();
            return;
        }
        if (info.event.url) {
            info.jsEvent.preventDefault();
            this.openUrl(info.event.url, info.event.title);
        }
    }

    /**
     * Refetches events. `fromCache` re-filters the last feed response without a
     * request (used by the source toggles).
     */
    refetch(fromCache = false) {
        if (!this.calendar) return;
        this.useCacheOnce = !!fromCache;
        this.calendar.refetchEvents();
    }

    // ── source toggles ───────────────────────────────────────────────────────

    /**
     * Wires the toggles. Their checked state is the source of truth for hidden
     * sources — it survives a re-init (React prop change) where the config would not.
     */
    bindSources() {
        this.sourceListeners = [];
        this.root.querySelectorAll(':scope > .dcms-calendar-sources .dcms-calendar-source').forEach((label) => {
            const swatch = label.querySelector('.dcms-calendar-source-swatch');
            if (swatch && swatch.dataset.color) {
                label.style.setProperty('--dcms-calendar-source-color', swatch.dataset.color);
            }
            const input = label.querySelector('.dcms-calendar-source-input');
            if (!input) return;
            if (input.checked) this.hiddenSources.delete(String(input.value));
            else this.hiddenSources.add(String(input.value));

            const listener = () => this.setSourceActive(input.value, input.checked);
            input.addEventListener('change', listener);
            this.sourceListeners.push([input, listener]);
        });
    }

    setSourceActive(id, active) {
        const key = String(id);
        if (active) this.hiddenSources.delete(key);
        else this.hiddenSources.add(key);
        this.refetch(true);
        this.emit('sourcetoggle', { id: key, active: !!active });

        if (this.config.sourceToggleUrl) {
            const body = new URLSearchParams({ id: key, active: active ? '1' : '0' });
            fetch(this.config.sourceToggleUrl, { method: 'POST', credentials: 'same-origin', body })
                .catch((e) => console.error('[dcms-calendar] source toggle not saved', e));
        }
    }

    // ── links / modal ────────────────────────────────────────────────────────

    openUrl(url, title) {
        const target = this.config.linkTarget || 'modal';
        if (target === 'blank') {
            window.open(url, '_blank', 'noopener');
            return;
        }
        if (target === 'top') {
            try {
                window.top.location.href = url;
            } catch (e) {
                window.location.href = url;
            }
            return;
        }
        if (target === 'modal' && this.openModal(url, title)) {
            return;
        }
        window.location.href = url;
    }

    /**
     * Opens the URL in the DCMS iframe modal (iziModal in the top window).
     * Returns false when the modal layer is not on the page (e.g. a bare React app).
     */
    openModal(url, title) {
        const modals = window.APP && window.APP.modalsInstance;
        if (!modals || typeof modals.openIziModal !== 'function') {
            return false;
        }

        if (!this.modalCallbackName) {
            this.modalCallbackName = '__dcmsCalendarModalClosed' + (++modalCallbackCounter);
            window[this.modalCallbackName] = () => {
                this.modalOpen = false;
                this.refetch(false);
            };
        }

        const trigger = document.createElement('a');
        trigger.href = url;
        trigger.setAttribute('data-dcms-modal', 'izimodal');
        trigger.setAttribute('data-dcms-izimodal-iframe', 'true');
        trigger.setAttribute('data-dcms-izimodal-iframeurl', url);
        trigger.setAttribute('data-dcms-izimodal-title', title || '');
        trigger.setAttribute('data-dcms-izimodal-width', this.config.modalWidth || '900px');
        trigger.setAttribute('data-dcms-izimodal-iframeheight', String(MODAL_INITIAL_HEIGHT));
        trigger.setAttribute('data-dcms-izimodal-autoheight', 'true');
        // No headercolor: the window follows the portal's header style (Modals.applyHeaderStyle())
        trigger.setAttribute('data-dcms-izimodal-onclosed', this.modalCallbackName);
        if (this.config.modalBorderBottom === false) {
            trigger.setAttribute('data-dcms-izimodal-borderbottom', 'false');
        }

        try {
            modals.openIziModal(trigger);
            this.modalOpen = true;
            return true;
        } catch (e) {
            console.error('[dcms-calendar] modal failed', e);
            return false;
        }
    }

    /**
     * A DCMS dataset form saved in the modal this calendar opened broadcasts
     * `dcms:recordset:refresh` (editHandler / deleteHandler). On a list page the
     * list closes the modal; on a page with only a calendar (dashboard) nobody
     * would — so the calendar closes it, shows the toast and refetches.
     */
    onWindowMessage(event) {
        if (!this.modalOpen || !event.data || event.data.type !== 'dcms:recordset:refresh') {
            return;
        }
        this.modalOpen = false;

        // The top-most window with APP owns the modal and the toasts.
        let appWin = window;
        try {
            let current = window;
            while (current !== window.top) {
                current = current.parent;
                if (current.APP) appWin = current;
            }
        } catch (e) { /* cross-origin parent — keep this window */ }

        try {
            if (appWin.APP && appWin.APP.modalsInstance) appWin.APP.modalsInstance.closeCurrentModal();
        } catch (e) { /* modal already gone */ }

        // The message is plain text and iziToast inserts it as HTML — escaped first (the
        // message may come from any window); a line break is a new line of the toast
        if (event.data.message && typeof appWin.showToast === 'function') {
            const isDelete = event.data.action === 'delete';
            const escaper = document.createElement('div');
            escaper.textContent = String(event.data.message);
            appWin.showToast('', escaper.innerHTML.replace(/\n/g, '<br>'), isDelete ? 'yellow' : 'green', isDelete ? 'delete' : 'check_circle');
        }

        this.refetch(false);
    }

    // ── state / DOM helpers ──────────────────────────────────────────────────

    setBusy(busy) {
        this.grid.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.root.classList.toggle('dcms-calendar-is-loading', !!busy);
    }

    showError(message) {
        if (!this.errorBox) return;
        this.errorBox.textContent = message;
        this.errorBox.hidden = false;
    }

    hideError() {
        if (this.errorBox && !this.errorBox.hidden) {
            this.errorBox.hidden = true;
            this.errorBox.textContent = '';
        }
    }

    emit(name, detail, cancelable = false) {
        const event = new CustomEvent('dcms:calendar:' + name, { bubbles: true, cancelable, detail });
        return this.root.dispatchEvent(event);
    }

    destroy() {
        this.destroyed = true;
        (this.sourceListeners || []).forEach(([input, listener]) => input.removeEventListener('change', listener));
        this.sourceListeners = [];
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.calendar) this.calendar.destroy();
        if (this.modalCallbackName) delete window[this.modalCallbackName];
        window.removeEventListener('message', this.messageListener);
        this.calendar = null;
        instances.delete(this.root);
    }
}

// ─── public API ──────────────────────────────────────────────────────────────

function resolveRoot(elOrId) {
    if (!elOrId) return null;
    if (typeof elOrId === 'string') return document.getElementById(elOrId);
    return elOrId;
}

/**
 * Initialises a calendar root (idempotent). Passing `config` to an already
 * initialised root re-creates it with the new configuration (React prop changes).
 *
 * @param {HTMLElement|string} elOrId
 * @param {object} [config]
 * @returns {DCMSEventCalendar|null}
 */
export function initEventCalendar(elOrId, config) {
    const root = resolveRoot(elOrId);
    if (!root) return null;

    const existing = instances.get(root);
    if (existing && !config) return existing;
    if (existing) existing.destroy();

    const instance = new DCMSEventCalendar(root, config);
    instances.set(root, instance);
    return instance;
}

export function initEventCalendars(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach((el) => {
        if (instances.has(el)) return;
        try {
            initEventCalendar(el);
        } catch (e) {
            console.error('[dcms-calendar] init failed', el, e);
        }
    });
}

window.DCMSCalendarEngine = {
    init: initEventCalendar,
    get: (elOrId) => instances.get(resolveRoot(elOrId)) || null,
    refetch: (elOrId) => {
        const instance = instances.get(resolveRoot(elOrId));
        if (instance) instance.refetch(false);
    },
    destroy: (elOrId) => {
        const instance = instances.get(resolveRoot(elOrId));
        if (instance) instance.destroy();
    },
    ensureEngine,
};

function boot() {
    initEventCalendars(document);

    // Calendars injected later (AJAX dataset render, formBuilder, dashboard widgets).
    const mo = new MutationObserver((records) => {
        records.forEach((r) => {
            r.addedNodes.forEach((n) => {
                if (n.nodeType !== 1) return;
                if (n.matches && n.matches(ROOT_SELECTOR)) initEventCalendars(n.parentNode);
                else if (n.querySelector && n.querySelector(ROOT_SELECTOR)) initEventCalendars(n);
            });
        });
    });
    mo.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
